import {
  useV2CreateSalesExecutiveMutation,
  useV2UpdateSalesExecutiveMutation,
  useV2DeleteSalesExecutiveMutation,
} from "@generated";
import type { V2SalesExecutiveResource } from "@generated";
import type { SalesExecutiveFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2SalesExecutiveResource is inferred from auth server-side, so we
// omit it and cast (same pattern as the BusinessUnits/Vendors MSPAs).
export const buildSalesExecutiveData = (
  values: SalesExecutiveFormValues,
  id?: string
): V2SalesExecutiveResource => {
  return {
    type: "sales-executives",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      title: values.title || "",
      phone: values.phone || "",
      email: values.email || "",
    },
  } as unknown as V2SalesExecutiveResource;
};

export const useSalesExecutiveMutations = () => {
  const [create, createState] = useV2CreateSalesExecutiveMutation();
  const [update, updateState] = useV2UpdateSalesExecutiveMutation();
  const [destroy, destroyState] = useV2DeleteSalesExecutiveMutation();

  return {
    create: (values: SalesExecutiveFormValues) =>
      create({ body: { data: buildSalesExecutiveData(values) } }).unwrap(),

    update: (id: string, values: SalesExecutiveFormValues) =>
      update({
        id: id,
        body: { data: buildSalesExecutiveData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    // `active: true` re-activates a soft-deleted contact (matches the existing
    // restore behavior and the BusinessUnits/Vendors pattern).
    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "sales-executives",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2SalesExecutiveResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useSalesExecutiveMutations;
